import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/db';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail loudly in dev rather than silently producing a broken client.
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Copy .env.example to .env.local and fill in your Supabase project values.',
  );
}

// ─── Resilient fetch ────────────────────────────────────────────────────────
//
// Every Supabase request (data, auth, storage) flows through this fetch.
// It absorbs the transient network blips that caused the intermittent
// "loading error": a request fired the instant the tab wakes, before Wi-Fi
// has actually reconnected, fails once — then everything works seconds later.
//
// Behaviour:
//   • GET/HEAD (reads) — retried up to 3× with backoff on network failure or
//     502/503/504/429 responses. If the browser is offline, it WAITS for the
//     'online' event (up to 4s) before retrying instead of burning attempts
//     on a dead connection.
//   • POST/PATCH/DELETE (writes) — NEVER auto-retried. Retrying a write could
//     duplicate an insert; failures surface to the caller as before.
//
// This protects every module in the portal — present and future — without
// touching any of them.

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function waitForOnline(maxMs: number): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener('online', done);
      clearTimeout(t);
      resolve();
    };
    const t = setTimeout(done, maxMs);
    window.addEventListener('online', done);
  });
}

const RETRYABLE_STATUS = new Set([502, 503, 504, 429]);

async function resilientFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const isRead = method === 'GET' || method === 'HEAD';
  const attempts = isRead ? 3 : 1;
  // A request that HANGS (typical right after the laptop wakes — the old
  // connection is half-dead) previously froze the page on "Loading…" until a
  // distant timeout. Each attempt now gets its own deadline; a hung READ is
  // aborted and retried on a fresh connection.
  const attemptTimeout = isRead ? 10_000 : 40_000;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    const ctrl = new AbortController();
    const onCallerAbort = () => ctrl.abort();
    init?.signal?.addEventListener?.('abort', onCallerAbort);
    const timer = setTimeout(() => ctrl.abort(), attemptTimeout);
    try {
      const res = await fetch(input, { ...init, signal: ctrl.signal });
      if (isRead && RETRYABLE_STATUS.has(res.status) && i < attempts - 1) {
        await sleep(300 * 2 ** i + Math.random() * 200);
        continue;
      }
      return res;
    } catch (e) {
      // TypeError('Failed to fetch'), AbortError (hung request) & friends.
      lastErr = e;
      if (init?.signal?.aborted) break; // the CALLER cancelled — don't retry
      if (i === attempts - 1) break;
      await waitForOnline(4_000);
      await sleep(300 * 2 ** i + Math.random() * 200);
    } finally {
      clearTimeout(timer);
      init?.signal?.removeEventListener?.('abort', onCallerAbort);
    }
  }
  throw lastErr;
}

// One-time marker so a deployed build is verifiable: open DevTools -> Console
// and look for this line. If it's absent, this file is NOT live yet.
// eslint-disable-next-line no-console
console.info('[liteline] resilient network layer v2 active');

// ─── Client ─────────────────────────────────────────────────────────────────

// ─── Remember-me aware auth storage ─────────────────────────────────────────
// "Remember me" ON  -> session lives in localStorage (survives closing the browser).
// "Remember me" OFF -> session lives in sessionStorage (cleared when the browser
//                      closes) but STILL survives reloads within the session.
// The login page sets the flag below before signing in. Reads check both stores,
// which also migrates any previously-stored session seamlessly.
export const REMEMBER_KEY = 'liteline.remember';

const authStorage = {
  getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      const remember = window.localStorage.getItem(REMEMBER_KEY) !== '0';
      if (remember) {
        window.localStorage.setItem(key, value);
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
        window.localStorage.removeItem(key);
      }
    } catch { /* storage full/blocked — session stays in memory */ }
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch { /* ignore */ }
  },
};

export const supabase = createClient<Database>(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: authStorage,
    storageKey: 'liteline.auth',
  },
  realtime: {
    params: { eventsPerSecond: 5 },
  },
  global: {
    fetch: resilientFetch,
  },
});
